import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Input, InputProps } from '@salecycle/ui-component-library';
import { InfoTooltip } from './InfoTooltip';
import { TooltipWithTitleProps } from '@salecycle/experimental/TooltipWithTitle';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing1};
`;

export type NameInputProps = Omit<InputProps, 'ariaLabel'> & {
  title: string;
  name: string;
  onChange: (updatedName: string) => void;
  tooltip?: Pick<TooltipWithTitleProps, 'title' | 'content'>;
};

const MAX_CHARACTERS = 100;

export const NameInput = ({ title, name, onChange, tooltip, ...props }: NameInputProps) => {
  const theme = useTheme();

  const previousValue = useRef(name);
  const [focusMode, setFocusMode] = useState(false);

  /*
    This forces the name to be updated at where we're tracking it in state.
    It's unknown where the name is stored, so we call `onChange` to force
    an update. 

    We need to do this due to issues with replacing an empty name with the stored
    ref. It won't work properly as when we save we use the value stored
    in state, so we need to make sure that state is updated with the last valid value
    that's stored in the ref. 
  */
  useEffect(() => {
    if (focusMode || name?.trim() || !previousValue.current?.trim()) {
      return;
    }

    if (!name?.trim()) {
      onChange(previousValue.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMode]);

  const onChangeCallback = useCallback(
    (value: string, e: ChangeEvent<HTMLInputElement>) => {
      if (value.length > MAX_CHARACTERS) {
        return;
      }

      previousValue.current = value.trim() ? value : previousValue.current;
      onChange(value, e);
    },
    [onChange],
  );

  const onFocus = useCallback(() => setFocusMode(true), []);
  const onBlur = useCallback(() => setFocusMode(false), []);

  return (
    <Layout>
      <span style={{ display: 'flex' }}>
        <span style={{ font: theme.designToken.body2 }}>{title}</span>
        {tooltip ? (
          <>
            <span style={{ marginRight: theme.designToken.spacing1 }}></span>
            <InfoTooltip title={tooltip.title} content={tooltip.content} displayInline />
          </>
        ) : null}
      </span>
      <Input
        ariaLabel={props['aria-label']}
        value={name}
        onChange={onChangeCallback}
        onFocus={onFocus}
        onBlur={onBlur}
        focusMode={focusMode}
        charLength={focusMode && name?.length}
        spacingBelow={false}
      />
      <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
        {name?.length ?? 0}/{MAX_CHARACTERS}
      </div>
    </Layout>
  );
};
